import fs from 'node:fs';
import path from 'node:path';
import { pathToFileURL } from 'node:url';

export interface ProviderDefinition {
  name: string;
  packageName: string;
  exportName: string;
  label: string;
  envKeys: string[];
}

export interface ProviderModelInfo {
  id: string;
  name: string;
  description?: string;
  contextLength?: number;
  slug?: string;
}

export interface ProviderModelsQuery {
  search?: string;
  page?: number;
  perPage?: number;
}

export interface ProviderModelsPage {
  models: ProviderModelInfo[];
  hasNextPage: boolean;
}

export interface ProviderFactoryConfig {
  apiKey: string;
  baseUrl?: string;
  timeout?: number;
  accountId?: string;
}

export interface ProviderInstance {
  getModels?: (filter?: string) => Promise<ProviderModelInfo[]>;
  getModelsPage?: (query?: ProviderModelsQuery) => Promise<ProviderModelsPage>;
}

export type ProviderFactory = (config: ProviderFactoryConfig) => ProviderInstance;

export interface DetectedProvider extends ProviderDefinition {
  declared: boolean;
  installed: boolean;
  configured: boolean;
  missingEnvKeys: string[];
}

export interface AvailableProviderModel {
  value: string;
  label: string;
  description: string;
  contextLength?: number;
  slug?: string;
}

const UUID_LIKE_MODEL_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const CLOUDFLARE_PROVIDER_MODEL_ID = /^@cf\//i;
const PAGE_SIZE = 100;
const MAX_PAGES = 1000;

export const CURRENT_MODELS_URLS = [
  'https://standardagentspec.org/models.md',
  'https://standardagentspec.com/models.md',
];

export const PROVIDER_DEFINITIONS: ProviderDefinition[] = [
  {
    name: 'anthropic',
    packageName: '@standardagents/anthropic',
    exportName: 'anthropic',
    label: 'Anthropic',
    envKeys: ['ANTHROPIC_API_KEY'],
  },
  {
    name: 'cloudflare',
    packageName: '@standardagents/cloudflare',
    exportName: 'cloudflare',
    label: 'Cloudflare Workers AI',
    envKeys: ['CLOUDFLARE_API_TOKEN', 'CLOUDFLARE_ACCOUNT_ID'],
  },
  {
    name: 'cerebras',
    packageName: '@standardagents/cerebras',
    exportName: 'cerebras',
    label: 'Cerebras',
    envKeys: ['CEREBRAS_API_KEY'],
  },
  {
    name: 'deepinfra',
    packageName: '@standardagents/deepinfra',
    exportName: 'deepinfra',
    label: 'DeepInfra',
    envKeys: ['DEEPINFRA_API_KEY'],
  },
  {
    name: 'google',
    packageName: '@standardagents/google',
    exportName: 'google',
    label: 'Google Gemini',
    envKeys: ['GOOGLE_API_KEY'],
  },
  {
    name: 'groq',
    packageName: '@standardagents/groq',
    exportName: 'groq',
    label: 'Groq',
    envKeys: ['GROQ_API_KEY'],
  },
  {
    name: 'novita',
    packageName: '@standardagents/novita',
    exportName: 'novita',
    label: 'Novita AI',
    envKeys: ['NOVITA_API_KEY'],
  },
  {
    name: 'openai',
    packageName: '@standardagents/openai',
    exportName: 'openai',
    label: 'OpenAI',
    envKeys: ['OPENAI_API_KEY'],
  },
  {
    name: 'openrouter',
    packageName: '@standardagents/openrouter',
    exportName: 'openrouter',
    label: 'OpenRouter',
    envKeys: ['OPENROUTER_API_KEY'],
  },
  {
    name: 'typesafe',
    packageName: '@standardagents/typesafe',
    exportName: 'typesafe',
    label: 'TypeSafe AI',
    envKeys: ['TYPESAFE_AI_API_KEY'],
  },
  {
    name: 'xai',
    packageName: '@standardagents/xai',
    exportName: 'xai',
    label: 'xai',
    envKeys: ['XAI_API_KEY'],
  },
];

function isOpaqueModelId(modelId?: string): boolean {
  if (!modelId) return true;
  return UUID_LIKE_MODEL_ID.test(modelId) || CLOUDFLARE_PROVIDER_MODEL_ID.test(modelId);
}

export function getProviderDefinition(name: string): ProviderDefinition | undefined {
  return PROVIDER_DEFINITIONS.find((provider) => provider.name === name);
}

export function parseEnvFile(content: string): Record<string, string> {
  const result: Record<string, string> = {};

  for (const line of content.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) continue;

    const withoutExport = trimmed.startsWith('export ')
      ? trimmed.slice('export '.length).trim()
      : trimmed;

    const eqIndex = withoutExport.indexOf('=');
    if (eqIndex === -1) continue;

    const key = withoutExport.slice(0, eqIndex).trim();
    let value = withoutExport.slice(eqIndex + 1).trim();

    if (value.startsWith('"') && value.endsWith('"')) {
      value = value.slice(1, -1).replace(/\\"/g, '"');
    } else if (value.startsWith("'") && value.endsWith("'")) {
      value = value.slice(1, -1);
    }

    result[key] = value;
  }

  return result;
}

export function findProjectRoot(startDir = process.cwd()): string {
  let currentDir = path.resolve(startDir);

  while (true) {
    if (fs.existsSync(path.join(currentDir, 'package.json'))) {
      return currentDir;
    }

    const parentDir = path.dirname(currentDir);
    if (parentDir === currentDir) {
      return path.resolve(startDir);
    }

    currentDir = parentDir;
  }
}

export function readProjectPackageJson(projectRoot: string): Record<string, unknown> {
  const packagePath = path.join(projectRoot, 'package.json');
  if (!fs.existsSync(packagePath)) {
    return {};
  }

  return JSON.parse(fs.readFileSync(packagePath, 'utf8')) as Record<string, unknown>;
}

export function getDeclaredProviderPackages(projectRoot: string): string[] {
  const pkg = readProjectPackageJson(projectRoot);
  const combined = {
    ...((pkg.dependencies as Record<string, string> | undefined) ?? {}),
    ...((pkg.devDependencies as Record<string, string> | undefined) ?? {}),
    ...((pkg.optionalDependencies as Record<string, string> | undefined) ?? {}),
    ...((pkg.peerDependencies as Record<string, string> | undefined) ?? {}),
  };

  return PROVIDER_DEFINITIONS
    .map((provider) => provider.packageName)
    .filter((packageName) => typeof combined[packageName] === 'string');
}

function resolvePackageEntry(packageName: string, projectRoot: string): string | null {
  const packageDir = findInstalledPackageDir(packageName, projectRoot);
  if (!packageDir) {
    return null;
  }

  const packageJsonPath = path.join(packageDir, 'package.json');

  try {
    const packageJson = JSON.parse(fs.readFileSync(packageJsonPath, 'utf8')) as {
      exports?: string | Record<string, unknown>;
      main?: string;
    };

    const exportPath = resolvePackageExportPath(packageJson.exports) || packageJson.main || 'index.js';
    return path.join(packageDir, exportPath);
  } catch {
    return null;
  }
}

function findInstalledPackageDir(packageName: string, projectRoot: string): string | null {
  let currentDir = path.resolve(projectRoot);

  while (true) {
    const candidate = path.join(currentDir, 'node_modules', ...packageName.split('/'));
    if (fs.existsSync(candidate)) {
      return candidate;
    }

    const parentDir = path.dirname(currentDir);
    if (parentDir === currentDir) {
      return null;
    }

    currentDir = parentDir;
  }
}

function resolvePackageExportPath(exportsField: unknown): string | null {
  if (typeof exportsField === 'string') {
    return exportsField;
  }

  if (!exportsField || typeof exportsField !== 'object') {
    return null;
  }

  const rootExport = (exportsField as Record<string, unknown>)['.'];
  if (typeof rootExport === 'string') {
    return rootExport;
  }

  if (!rootExport || typeof rootExport !== 'object') {
    return null;
  }

  const rootExportRecord = rootExport as Record<string, unknown>;
  const candidate = rootExportRecord.import || rootExportRecord.default || rootExportRecord.require;
  return typeof candidate === 'string' ? candidate : null;
}

export function isProviderPackageInstalled(packageName: string, projectRoot: string): boolean {
  return resolvePackageEntry(packageName, projectRoot) !== null;
}

export function loadProjectEnv(
  projectRoot: string,
  processEnv: NodeJS.ProcessEnv = process.env
): Record<string, string> {
  const result: Record<string, string> = {};
  const envFiles = ['.env', '.env.local', '.dev.vars', '.dev.vars.local'];

  for (const fileName of envFiles) {
    const filePath = path.join(projectRoot, fileName);
    if (!fs.existsSync(filePath)) continue;
    Object.assign(result, parseEnvFile(fs.readFileSync(filePath, 'utf8')));
  }

  for (const [key, value] of Object.entries(processEnv)) {
    if (typeof value === 'string') {
      result[key] = value;
    }
  }

  return result;
}

export function detectProviders(
  projectRoot: string,
  env: Record<string, string> = loadProjectEnv(projectRoot)
): DetectedProvider[] {
  const declaredPackages = new Set(getDeclaredProviderPackages(projectRoot));

  return PROVIDER_DEFINITIONS
    .filter((provider) => declaredPackages.has(provider.packageName))
    .map((provider) => {
      const installed = isProviderPackageInstalled(provider.packageName, projectRoot);
      const missingEnvKeys = provider.envKeys.filter((envKey) => !env[envKey]);

      return {
        ...provider,
        declared: true,
        installed,
        configured: installed && missingEnvKeys.length === 0,
        missingEnvKeys,
      };
    });
}

export async function loadProviderFactory(
  provider: ProviderDefinition,
  projectRoot: string
): Promise<ProviderFactory> {
  const entryPath = resolvePackageEntry(provider.packageName, projectRoot);
  if (!entryPath) {
    throw new Error(`Provider package ${provider.packageName} is not installed in ${projectRoot}.`);
  }

  const moduleUrl = entryPath.startsWith('file:')
    ? entryPath
    : pathToFileURL(entryPath).href;
  const providerModule = await import(moduleUrl) as Record<string, unknown>;
  const factory = providerModule[provider.exportName];

  if (typeof factory !== 'function') {
    throw new Error(`Provider package ${provider.packageName} does not export ${provider.exportName}().`);
  }

  return factory as ProviderFactory;
}

export function buildProviderConfig(
  provider: ProviderDefinition,
  env: Record<string, string>
): ProviderFactoryConfig {
  const apiKey = env[provider.envKeys[0]] || '';

  if (provider.name === 'cloudflare') {
    const accountId = env.CLOUDFLARE_ACCOUNT_ID;
    return {
      apiKey,
      accountId,
      baseUrl: accountId
        ? `https://api.cloudflare.com/client/v4/accounts/${accountId}/ai/v1`
        : undefined,
    };
  }

  return { apiKey };
}

export async function collectProviderModels(
  providerName: string,
  providerInstance: ProviderInstance
): Promise<AvailableProviderModel[]> {
  const rawModels: ProviderModelInfo[] = [];

  if (providerInstance.getModelsPage) {
    for (let page = 1; page <= MAX_PAGES; page += 1) {
      const result = await providerInstance.getModelsPage({ page, perPage: PAGE_SIZE });
      rawModels.push(...result.models);
      if (!result.hasNextPage) {
        return normalizeProviderModels(providerName, rawModels);
      }
    }

    throw new Error(`Provider ${providerName} exceeded ${MAX_PAGES} model pages without terminating pagination.`);
  }

  if (providerInstance.getModels) {
    rawModels.push(...await providerInstance.getModels());
    return normalizeProviderModels(providerName, rawModels);
  }

  throw new Error(`Provider ${providerName} does not support model discovery.`);
}

export async function fetchAvailableModelsForProvider(
  providerName: string,
  projectRoot: string,
  env: Record<string, string>,
  factoryLoader: (provider: ProviderDefinition, projectRoot: string) => Promise<ProviderFactory> = loadProviderFactory
): Promise<AvailableProviderModel[]> {
  const provider = getProviderDefinition(providerName);
  if (!provider) {
    throw new Error(`Unknown provider "${providerName}".`);
  }

  const factory = await factoryLoader(provider, projectRoot);
  const providerInstance = factory(buildProviderConfig(provider, env));
  return collectProviderModels(provider.name, providerInstance);
}

export async function fetchCurrentModelsMarkdown(
  urls: string[] = CURRENT_MODELS_URLS
): Promise<{ url: string; content: string }> {
  let lastError: Error | null = null;

  for (const url of urls) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status} ${response.statusText}`.trim());
      }

      return {
        url,
        content: await response.text(),
      };
    } catch (error) {
      lastError = error instanceof Error
        ? new Error(`Failed to fetch ${url}: ${error.message}`)
        : new Error(`Failed to fetch ${url}.`);
    }
  }

  throw lastError ?? new Error('No current models URLs configured.');
}

export function buildProviderSelectionHelp(providers: DetectedProvider[]): string {
  const providerNames = providers.map((provider) => provider.name).join(', ');
  const examples = providers
    .map((provider) => `  agents available-models --provider=${provider.name}`)
    .join('\n');

  return [
    `Multiple configured providers were found: ${providerNames}`,
    'Re-run with a specific provider to fetch all model IDs:',
    examples,
  ].join('\n');
}

export function describeProviderAvailability(provider: DetectedProvider): string {
  if (!provider.installed) {
    return `${provider.name}: not installed`;
  }

  if (provider.missingEnvKeys.length > 0) {
    return `${provider.name}: missing ${provider.missingEnvKeys.join(', ')}`;
  }

  return `${provider.name}: unavailable`;
}

export function formatAvailableModelsOutput(
  provider: ProviderDefinition,
  models: AvailableProviderModel[]
): string {
  const lines = [
    `# ${provider.label} models`,
    '',
    'Use the `model` values exactly as shown in `defineModel({ model: ... })`.',
    '',
    '| model | label | context | notes |',
    '| --- | --- | ---: | --- |',
  ];

  for (const model of models) {
    const context = model.contextLength ? model.contextLength.toLocaleString('en-US') : '';
    lines.push(
      `| \`${escapeTableCell(model.value)}\` | ${escapeTableCell(model.label)} | ${context} | ${escapeTableCell(model.description)} |`
    );
  }

  return `${lines.join('\n')}\n`;
}

export function normalizeProviderModels(
  providerName: string,
  rawModels: ProviderModelInfo[]
): AvailableProviderModel[] {
  const deduped = new Map<string, AvailableProviderModel>();

  for (const model of rawModels) {
    const value = getExecutableModelId(providerName, model);
    if (!value) continue;

    if (!deduped.has(value)) {
      deduped.set(value, {
        value,
        label: model.name || value,
        description: model.description || '',
        contextLength: model.contextLength,
        slug: model.slug,
      });
    }
  }

  return [...deduped.values()].sort((left, right) => left.value.localeCompare(right.value));
}

function getExecutableModelId(
  providerName: string,
  model: Pick<ProviderModelInfo, 'id' | 'name' | 'slug'>
): string {
  if (model.id && !isOpaqueModelId(model.id)) {
    return model.id;
  }

  if (providerName === 'cloudflare') {
    const publicCloudflareId = [model.name, model.slug]
      .find((value): value is string => typeof value === 'string' && value.includes('/'));

    if (publicCloudflareId) {
      return publicCloudflareId.startsWith('@cf/')
        ? publicCloudflareId
        : `@cf/${publicCloudflareId.replace(/^@cf\//, '')}`;
    }
  }

  return model.slug || model.name || model.id;
}

function escapeTableCell(value: string): string {
  return value.replace(/\|/g, '\\|').replace(/\n/g, '<br />');
}
